'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { cn } from '@core/utils';
import type { QueryKey } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
interface InfiniteListWithMoreButtonProps<TData> {
  /**
   * React Query에서 사용할 쿼리 키
   * 이 키를 기준으로 쿼리 캐싱 및 무효화가 이루어짐
   */
  queryKey: QueryKey;
  /**
   * 데이터를 가져오는 비동기 함수
   * @param pageParam - 현재 페이지 파라미터(오프셋)
   * @returns Promise<TData[]> - 데이터 배열을 반환하는 Promise
   */
  queryFn: ({
    pageParam
  }: {
    pageParam: number;
  }) => Promise<TData[]>;
  /**
   * 각 아이템을 렌더링하는 함수
   * @param item - 렌더링할 아이템 데이터
   * @param index - 아이템의 인덱스
   * @returns ReactNode - 렌더링할 컴포넌트
   */
  renderItem: (item: TData, index: number) => ReactNode;
  /**
   * 각 아이템의 고유 키를 생성하는 함수
   * 제공되지 않을 경우 기본적으로 item.id 또는 index를 사용
   * @param item - 키를 생성할 아이템 데이터
   * @param index - 아이템의 인덱스
   * @returns string | number - 고유 키 값
   */
  getItemKey: (item: TData, index: number) => string | number;
  /**
   * 한 페이지에 표시할 아이템 수
   */
  pageSize: number;
  /**
   * 로딩 상태를 표시하는 컴포넌트
   */
  loadingComponent?: ReactNode;
  /**
   * 추가 데이터 로딩 상태를 표시하는 컴포넌트
   */
  fetchingNextPageComponent?: ReactNode;
  /**
   * 더보기 버튼
   */
  moreButtonComponent: ReactNode;
  moreButtonClassName?: string;
  /**
   * 초기 데이터
   * 서버/초기 렌더링 시 HTML에 포함
   */
  initialData?: TData[];
}
export const InfiniteListWithMoreButton = t0 => {
  const $ = _c(42);
  const {
    queryKey,
    queryFn,
    renderItem,
    getItemKey,
    pageSize,
    loadingComponent,
    fetchingNextPageComponent,
    moreButtonComponent,
    moreButtonClassName,
    initialData
  } = t0;
  const [initialQueryKey] = useState(queryKey);
  const isQueryKeyChanged = JSON.stringify(queryKey) !== JSON.stringify(initialQueryKey);
  const shouldUseInitialData = !isQueryKeyChanged ? initialData : undefined;
  let t1;
  if ($[0] !== queryFn) {
    t1 = t2 => {
      const {
        pageParam
      } = t2;
      return queryFn({
        pageParam
      });
    };
    $[0] = queryFn;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pageSize) {
    t2 = (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.length === 0 || lastPage.length < pageSize) {
        return;
      }
      return lastPageParam + 1;
    };
    $[2] = pageSize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== initialData || $[5] !== shouldUseInitialData) {
    t3 = shouldUseInitialData ? {
      pages: [initialData],
      pageParams: [1]
    } : undefined;
    $[4] = initialData;
    $[5] = shouldUseInitialData;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== queryKey || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey,
      queryFn: t1,
      getNextPageParam: t2,
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      initialData: t3
    };
    $[7] = queryKey;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error
  } = useInfiniteQuery(t4);
  let t5;
  if ($[12] !== getItemKey || $[13] !== renderItem || $[14] !== shouldUseInitialData) {
    t5 = shouldUseInitialData?.map((item, index) => <React.Fragment key={getItemKey(item, index)}>{renderItem(item, index)}</React.Fragment>);
    $[12] = getItemKey;
    $[13] = renderItem;
    $[14] = shouldUseInitialData;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== data?.pages || $[17] !== getItemKey || $[18] !== renderItem || $[19] !== shouldUseInitialData) {
    t6 = data?.pages?.slice(shouldUseInitialData ? 1 : 0).flat().filter(_temp).map((item_1, index_0) => <React.Fragment key={getItemKey(item_1, index_0)}>{renderItem(item_1, index_0)}</React.Fragment>);
    $[16] = data?.pages;
    $[17] = getItemKey;
    $[18] = renderItem;
    $[19] = shouldUseInitialData;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== isLoading || $[22] !== loadingComponent) {
    t7 = isLoading && (loadingComponent || <div className="flex-center" style={{
      gridColumn: "1 / -1"
    }}><Spinner /></div>);
    $[21] = isLoading;
    $[22] = loadingComponent;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== fetchingNextPageComponent || $[25] !== isFetchingNextPage) {
    t8 = isFetchingNextPage && (fetchingNextPageComponent || <div className="flex-center" style={{
      gridColumn: "1 / -1"
    }}><Spinner /></div>);
    $[24] = fetchingNextPageComponent;
    $[25] = isFetchingNextPage;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== fetchNextPage || $[28] !== hasNextPage || $[29] !== moreButtonClassName || $[30] !== moreButtonComponent) {
    t9 = hasNextPage !== false && React.isValidElement(moreButtonComponent) && <div className={cn("flex justify-center", moreButtonClassName)} style={{
      gridColumn: "1 / -1"
    }}>{React.cloneElement(moreButtonComponent as React.ReactElement<Record<string, unknown>>, {
        onClick: fetchNextPage
      })}</div>;
    $[27] = fetchNextPage;
    $[28] = hasNextPage;
    $[29] = moreButtonClassName;
    $[30] = moreButtonComponent;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== error?.message || $[33] !== isError) {
    t10 = isError && <div className="text-red-500 p-16 text-center">{error?.message || "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958"}</div>;
    $[32] = error?.message;
    $[33] = isError;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t11 = <>{t5}{t6}{t7}{t8}{t9}{t10}</>;
    $[35] = t10;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
};
function _temp(item_0) {
  return item_0 !== undefined;
}